/**
 * Aggregated form-level validation entry point.
 *
 * Delegates to the three domain validators and deep-merges their results so that
 * Formik receives a single flat error object.
 */
import { JobTitle } from '../../../../../api/job-titles'
import {
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormErrors,
} from '../workers-request-form.types'
import { validateMissionData } from './validate-mission-data'
import { validatePosts } from './validate-posts'
import { validateWorkPeriods } from './validate-work-periods'

/**
 * Dependencies required for the full validation pipeline.
 */
export interface ValidateWorkersRequestFormDeps {
  jobTitles: JobTitle[]
}

/**
 * Merges two error objects produced by the sub-validators.
 *
 * Rules:
 * - Primitive / string fields are overwritten by `source` when present.
 * - `missionData` is shallow-merged (the nested keys are independent).
 * - Dynamic `workPeriod-${isoDate}` arrays are concatenated.
 * - `meta` is shallow-merged as well.
 */
const mergeErrors = (
  target: WorkersRequestFormErrors,
  source: WorkersRequestFormErrors,
): WorkersRequestFormErrors => {
  const merged: WorkersRequestFormErrors = { ...target }

  for (const [key, value] of Object.entries(source)) {
    if (value === undefined) continue

    if (key === 'missionData') {
      merged.missionData = {
        ...(merged.missionData as Record<string, unknown> | undefined),
        ...(value as Record<string, unknown>),
      } as WorkersRequestFormErrors['missionData']
      continue
    }

    if (key === 'meta') {
      merged.meta = {
        ...(merged.meta as Record<string, unknown> | undefined),
        ...(value as Record<string, unknown>),
      } as WorkersRequestFormErrors['meta']
      continue
    }

    if (key.startsWith('workPeriod-')) {
      const existing = (merged as Record<string, unknown>)[key] as string[] | undefined
      const incoming = value as string[]
      ;(merged as Record<string, unknown>)[key] = existing ? [...existing, ...incoming] : incoming
      continue
    }

    ;(merged as Record<string, unknown>)[key] = value
  }

  return merged
}

/**
 * Full validation pipeline for the workers-request-form.
 *
 * @param values - Current Formik values.
 * @param deps - External dependencies (jobTitles cache for coefficient check).
 * @returns Aggregated errors object ready to be returned from Formik `validate`.
 */
export const validateWorkersRequestForm = (
  values: WorkersRequestCreateUpdateFormValues,
  deps: ValidateWorkersRequestFormDeps,
): WorkersRequestFormErrors => {
  let errors: WorkersRequestFormErrors = {}

  errors = mergeErrors(errors, validateMissionData(values))
  errors = mergeErrors(errors, validateWorkPeriods(values))
  errors = mergeErrors(errors, validatePosts(values, { jobTitles: deps.jobTitles }))

  return errors
}
